import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Grid, FormHelperText,
  Select, FormLabel, MenuItem, Box, Stack, Checkbox, FormControlLabel, Divider
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useFormik } from 'formik';
import * as yup from 'yup';
import ReactQuill from 'react-quill';
import { toast } from 'react-toastify';
import { apiget, apiput, apipost } from '../../service/api';

import 'react-quill/dist/quill.snow.css';

const CLOUD_UPLOAD_ENDPOINT = 'cloudinary/upload/image';

const stripHtml = (s = '') => s.replace(/<[^>]*>/g, '').trim();

const validationSchema = yup.object({
  title: yup.string().required('Title is required'),
  body: yup
    .string()
    .test('not-empty-html', 'Body is required', (v) => stripHtml(v || '').length > 0),
  categories: yup.array().min(1, 'At least one category is required'),
  callToActionTitle: yup.string().required('Call to action title is required'),
  callToActionButtonText: yup.string().required('Button text is required'),
  callToActionLink: yup.string().required('Call to action link is required'),
  previewBlurb: yup
    .string()
    .required('Preview blurb is required')
    .max(300, 'Preview must be 300 characters or less'),
});

const EditBlogPost = ({ open, handleClose, id, fetchPosts }) => {
  const [postData, setPostData] = useState({});
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const quillRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  const fetchPost = async () => {
    const res = await apiget(`blog/${id}`);
    if (res?.status === 200) {
      setPostData(res.data);
      setPreview(res.data.imageUrl || '');
    }
  };

  useEffect(() => {
    if (open) fetchPost();
  }, [open]);

  const uploadViaBackend = async (file) => {
    const fd = new FormData();
    fd.append('headshot', file);
    const res = await apipost(CLOUD_UPLOAD_ENDPOINT, fd, true);
    if (!res || res.status < 200 || res.status >= 300) throw new Error('Upload failed');
    return res.data.url || res.data.secure_url;
  };

  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const quill = quillRef.current?.getEditor();
      if (!quill) return;

      try {
        setUploading(true);
        quill.enable(false);
        const url = await uploadViaBackend(file);
        quill.enable(true);
        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength();
        quill.insertEmbed(index, 'image', url, 'user');
        quill.setSelection(index + 1);
        formik.setFieldValue('body', quill.root.innerHTML, true);
      } catch (e) {
        console.error(e);
        toast.error('Image upload failed');
        quill.enable(true);
      } finally {
        setUploading(false);
      }
    };
  };

  const handleOgUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        setUploading(true);
        const url = await uploadViaBackend(file);
        formik.setFieldValue('seoOgImage', url);
        toast.success('Social image uploaded');
      } catch (e) {
        toast.error('Image upload failed');
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: { image: handleImageUpload }
    },
    clipboard: { matchVisual: false },
  }), []);

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'link', 'image'
  ];

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: postData?.title || '',
      body: postData?.body || '',
      categories: Array.isArray(postData?.categories) ? postData.categories : [],
      imageUrl: postData?.imageUrl || '',
      callToActionTitle: postData?.callToActionTitle || '',
      callToActionButtonText: postData?.callToActionButtonText || '',
      callToActionLink: postData?.callToActionLink || '',
      previewBlurb: postData?.previewBlurb || '',
      slug: postData?.slug || '',
      published: postData?.published ?? false,
      seoTitle: postData?.seo?.title || '',
      seoDescription: postData?.seo?.description || '',
      seoOgImage: postData?.seo?.ogImage || ''
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        Object.entries(values).forEach(([key, value]) => {
          if (key === 'categories') {
            value.forEach((cat, i) => formData.append(`categories[${i}]`, cat));
          } else {
            formData.append(key, value);
          }
        });

        formData.append('modifiedBy', user?._id);
        formData.append('modifiedOn', new Date().toISOString());
        if (image) formData.append('headshot', image);

        const res = await apiput(`blog/${id}`, formData, true);
        if (res?.status === 200) {
          toast.success('Blog post updated successfully!');
          fetchPosts();
          handleClose();
        } else {
          toast.error('Failed to update post.');
        }
      } catch (err) {
        console.error(err);
        toast.error('Unexpected error updating post.');
      }
    }
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">Edit Blog Post</Typography>
        <ClearIcon onClick={handleClose} sx={{ cursor: 'pointer' }} />
      </DialogTitle>

      <DialogContent dividers>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormLabel>Title</FormLabel>
              <TextField
                fullWidth
                size="small"
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
              />
            </Grid>

            <Grid item xs={12}>
              <FormLabel>Preview Blurb</FormLabel>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                name="previewBlurb"
                value={formik.values.previewBlurb}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.previewBlurb && Boolean(formik.errors.previewBlurb)}
                helperText={formik.touched.previewBlurb && formik.errors.previewBlurb}
              />
            </Grid>

            <Grid item xs={12}>
              <FormLabel>Categories</FormLabel>
              <Select
                multiple fullWidth size="small" name="categories"
                value={formik.values.categories}
                onChange={formik.handleChange}
                error={formik.touched.categories && Boolean(formik.errors.categories)}
              >
                {['Industry News', 'Home Tips', 'Finance', 'Mortgage', 'Advice'].map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ color: 'error.main' }}>
                {formik.touched.categories && formik.errors.categories}
              </FormHelperText>
            </Grid>

            <Grid item xs={12}>
              <FormLabel>Body</FormLabel>
              <ReactQuill
                ref={quillRef}
                value={formik.values.body}
                onChange={(val) => formik.setFieldValue('body', val)}
                onBlur={() => formik.setFieldTouched('body', true)}
                modules={quillModules}
                formats={quillFormats}
                theme="snow"
                placeholder={uploading ? 'Uploading image…' : 'Write your post here'}
                readOnly={uploading}
              />
              {formik.touched.body && formik.errors.body && (
                <Typography sx={{ color: 'error.main' }}>{formik.errors.body}</Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormLabel>Current Image</FormLabel>
              {preview && (
                <Box mt={1}>
                  <img src={preview} alt="Preview" style={{ maxHeight: 180, borderRadius: 8 }} />
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <FormLabel>Upload New Image (cover)</FormLabel>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setImage(file);
                    setPreview(URL.createObjectURL(file));
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel>Call to Action Title</FormLabel>
              <TextField
                fullWidth
                size="small"
                name="callToActionTitle"
                value={formik.values.callToActionTitle}
                onChange={formik.handleChange}
                error={formik.touched.callToActionTitle && Boolean(formik.errors.callToActionTitle)}
                helperText={formik.touched.callToActionTitle && formik.errors.callToActionTitle}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel>Call to Action Button Text</FormLabel>
              <TextField
                fullWidth
                size="small"
                name="callToActionButtonText"
                value={formik.values.callToActionButtonText}
                onChange={formik.handleChange}
                error={formik.touched.callToActionButtonText && Boolean(formik.errors.callToActionButtonText)}
                helperText={formik.touched.callToActionButtonText && formik.errors.callToActionButtonText}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel>Call to Action Link</FormLabel>
              <TextField
                fullWidth
                size="small"
                name="callToActionLink"
                value={formik.values.callToActionLink}
                onChange={formik.handleChange}
                error={formik.touched.callToActionLink && Boolean(formik.errors.callToActionLink)}
                helperText={formik.touched.callToActionLink && formik.errors.callToActionLink}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="text.secondary">SEO &amp; URL</Typography>
              </Divider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel>URL slug</FormLabel>
              <TextField
                fullWidth
                size="small"
                name="slug"
                value={formik.values.slug}
                onChange={formik.handleChange}
                helperText="Leave blank to auto-generate. Changing a published post's slug keeps the old link working via a redirect."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                sx={{ mt: 3 }}
                control={
                  <Checkbox
                    name="published"
                    checked={!!formik.values.published}
                    onChange={formik.handleChange}
                  />
                }
                label="Published (visible on the public site)"
              />
            </Grid>

            <Grid item xs={12}>
              <FormLabel>SEO title (optional)</FormLabel>
              <TextField
                fullWidth
                size="small"
                name="seoTitle"
                value={formik.values.seoTitle}
                onChange={formik.handleChange}
                helperText="Shown in Google results. Falls back to the post title if blank."
              />
            </Grid>

            <Grid item xs={12}>
              <FormLabel>SEO meta description (optional)</FormLabel>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                name="seoDescription"
                value={formik.values.seoDescription}
                onChange={formik.handleChange}
                helperText="The text under the link in Google. Falls back to the preview blurb if blank."
              />
            </Grid>

            <Grid item xs={12}>
              <FormLabel>Social share image (optional)</FormLabel>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  fullWidth
                  size="small"
                  name="seoOgImage"
                  value={formik.values.seoOgImage}
                  onChange={formik.handleChange}
                  placeholder="https://…"
                />
                <Button variant="outlined" onClick={handleOgUpload} disabled={uploading}>
                  {uploading ? 'Uploading…' : 'Upload'}
                </Button>
              </Stack>
              <FormHelperText>Shown when the post is shared on Facebook/LinkedIn. Falls back to the cover image.</FormHelperText>
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={formik.handleSubmit} variant="contained">Update</Button>
        <Button onClick={handleClose} color="error" variant="outlined">Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditBlogPost;
